/** The partner's own developer keys (scope `partner_dev`, newest first, at most 20) and the
 *  expiry cap a key issued or renewed now gets. Each key opens its API credential, where it
 *  is disabled, revoked or renewed; the key itself is never shown — it went to the contact
 *  once, through the claim link. */
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import type { PartnerDetail } from '@/api/partners';
import { StatusChip } from '@/features/api-credentials/apiCredentialFormat';
import { formatDate, formatRelativeTime } from '@/utils/format';

import { Section } from './PartnerSection';

const mono = { fontFamily: 'monospace', fontSize: 13 } as const;

export function PartnerDeveloperKeysSection({ partner }: { partner: PartnerDetail }) {
  const keys = partner.developer_keys;
  return (
    <Section title="Developer keys">
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        {partner.key_expiry_cap
          ? `Keys issued or renewed now expire by ${formatDate(partner.key_expiry_cap)} — the shorter of the contract term and 180 days.`
          : 'The partner cannot hold keys now (see the gate above): no key can be issued or renewed.'}{' '}
        Disable, revoke or renew a key on its API credential.
      </Typography>
      {keys.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No developer key yet — send a claim link to a technical contact.
        </Typography>
      ) : (
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Developer keys">
            <TableHead>
              <TableRow>
                <TableCell>Key</TableCell>
                <TableCell>Prefix</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Expires</TableCell>
                <TableCell>Last used</TableCell>
                <TableCell>Created</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {keys.map((k) => {
                const expired = k.expires !== null && new Date(k.expires).getTime() < Date.now();
                return (
                  <TableRow key={k.id}>
                    <TableCell>
                      <RouterLink to={`/api-credentials/${k.id}`} style={{ color: '#0a1a3f', fontWeight: 500 }}>
                        {k.name}
                      </RouterLink>
                    </TableCell>
                    <TableCell sx={mono}>{k.key_prefix ? `${k.key_prefix}…` : '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={k.status} />
                    </TableCell>
                    <TableCell sx={expired ? { color: 'error.main' } : undefined}>
                      {k.expires ? formatDate(k.expires) : '—'}
                      {expired && ' (expired)'}
                    </TableCell>
                    <TableCell title={k.last_used ?? undefined}>
                      {k.last_used ? formatRelativeTime(k.last_used) : 'Never'}
                    </TableCell>
                    <TableCell>{formatDate(k.created_ts)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      )}
    </Section>
  );
}
